import {
  Archive,
  CalendarCheck,
  CalendarDays,
  ClipboardCheck,
  FileText,
  MessageSquare,
  PackageX,
  Scan,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  UserCog,
  type LucideIcon,
} from "lucide-react";

/**
 * Argumentaires du panneau de marque (écrans de connexion et d'inscription).
 *
 * QUATRE ANGLES, UN PAR MÉTIER DU COMPTOIR — l'opérationnel, l'équipe, la
 * conformité, le temps gagné. Un visiteur ne revient pas sur cet écran pour la
 * même raison qu'à sa première visite : le titulaire qui s'inscrit cherche à
 * organiser son équipe, le préparateur qui se connecte le matin cherche sa
 * journée. Faire tourner l'angle donne à chacun une chance de tomber sur celui
 * qui lui parle, là où un texte unique ne parle qu'à un seul.
 *
 * RÈGLE D'ÉCRITURE, tenue dans les quatre : chaque ligne décrit un RÉSULTAT
 * observable au comptoir, jamais une fonctionnalité. « Les ruptures se
 * signalent une fois pour toute l'équipe » se vérifie ; « module de gestion des
 * ruptures » ne se vérifie pas et ne se retient pas.
 *
 * RÈGLE DE VÉRITÉ : tout ce qui est promis ici existe dans l'application —
 * ordonnances et pièces jointes, scan, commandes, ruptures locales et
 * nationales, locations et suivi des paiements, contrôles qualité,
 * non-conformités, incidents, planning, congés, agenda, messagerie, actualités,
 * annuaire, notifications mobiles, archivage automatique, rôles et permissions.
 * Une promesse d'écran d'accueil que le produit ne tient pas se paie à la
 * première semaine d'essai.
 */
export interface PitchPoint {
  icon: LucideIcon;
  title: string;
  detail: string;
}

export interface Pitch {
  /** Titre court, deux lignes maximum à l'affichage. */
  headline: string;
  /** Une phrase qui situe le produit pour qui ne le connaît pas. */
  intro: string;
  points: PitchPoint[];
}

export const PITCHES: Pitch[] = [
  {
    headline: "Le comptoir ne tient plus sur des post-it.",
    intro:
      "Ordonnances en attente, commandes, ruptures, locations de matériel : tout ce qui traînait sur un carnet ou dans une tête devient consultable par l'équipe entière.",
    points: [
      {
        icon: FileText,
        title: "Une ordonnance en attente ne se perd plus",
        detail:
          "Photographiée, rattachée au patient, datée — et retrouvée en deux gestes quand il repasse.",
      },
      {
        icon: PackageX,
        title: "Une rupture se signale une fois, pour tout le monde",
        detail:
          "Fini les trois personnes qui appellent le même laboratoire. Alertes nationales comprises.",
      },
      {
        icon: ShoppingCart,
        title: "Commandes et locations suivies au même endroit",
        detail:
          "Ce qui est parti, ce qui revient, ce qui reste dû — sans rouvrir le classeur.",
      },
    ],
  },
  {
    headline: "Votre équipe sait quoi faire, sans vous le demander.",
    intro:
      "Planning, congés, consignes et transmissions : chacun voit ce qui le concerne depuis son téléphone, y compris ceux qui n'étaient pas là hier.",
    points: [
      {
        icon: CalendarDays,
        title: "Le planning cesse d'être un tableau blanc",
        detail:
          "Qui est là cette semaine, qui remplace qui — visible avant d'arriver à l'officine.",
      },
      {
        icon: CalendarCheck,
        title: "Les congés se demandent et se valident sans papier",
        detail:
          "La demande part, vous tranchez, le planning se met à jour tout seul.",
      },
      {
        icon: MessageSquare,
        title: "Les consignes arrivent au bon moment",
        detail:
          "Messagerie d'équipe et fil d'actualités, notifiés sur mobile — pas un groupe de discussion personnel.",
      },
    ],
  },
  {
    headline: "La conformité, sans le classeur.",
    intro:
      "Contrôles de température, non-conformités, incidents : ce que l'inspection demande est horodaté, attribué et retrouvable — parce que c'est saisi au moment où ça se passe.",
    points: [
      {
        icon: ClipboardCheck,
        title: "Les contrôles sont faits et prouvés",
        detail:
          "Assignés, rappelés, cochés par une personne identifiée. Pas de relevé reconstitué le soir.",
      },
      {
        icon: ShieldCheck,
        title: "Une non-conformité suit son cours jusqu'au bout",
        detail:
          "Déclarée, attribuée, résolue — avec la trace de qui a fait quoi, et quand.",
      },
      {
        icon: Archive,
        title: "L'historique se range tout seul",
        detail:
          "Archivage automatique par module : le courant reste lisible, l'ancien reste consultable.",
      },
    ],
  },
  {
    headline: "Une officine, un seul outil.",
    intro:
      "Pensé pour le comptoir, pas pour un bureau : tout se fait depuis un téléphone, entre deux clients, sans formation.",
    points: [
      {
        icon: Smartphone,
        title: "Utilisable debout, entre deux patients",
        detail:
          "Installable sur l'écran d'accueil, utilisable au comptoir comme en réserve.",
      },
      {
        icon: Scan,
        title: "Une ordonnance se saisit en la photographiant",
        detail:
          "Le texte est lu automatiquement : il reste à vérifier, pas à retaper.",
      },
      {
        icon: UserCog,
        title: "Chacun voit ce qui le regarde",
        detail:
          "Rôles et permissions réglés par le titulaire — un stagiaire n'a pas la vue d'un adjoint.",
      },
    ],
  },
];
